import { SectionHeader } from '../components/shared/SectionHeader';
import { CTAStrip } from '../components/shared/CTAStrip';
import {
  Target,
  Shield,
  Eye,
  Users,
  Leaf,
  Globe
} from 'lucide-react';

const TEAM_PLACEHOLDER = "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400";

const values = [
  {
    icon: Shield,
    title: "Integrity",
    description: "We build systems that maintain the highest standards of data accuracy and document authenticity."
  },
  {
    icon: Eye,
    title: "Transparency",
    description: "Complete visibility into certification processes, with audit trails and reporting for accountability."
  },
  {
    icon: Users,
    title: "Enablement",
    description: "Empowering farmers, exporters, and regulators with tools that make compliance achievable."
  },
  {
    icon: Globe,
    title: "Trade Facilitation",
    description: "Breaking down barriers to international agricultural trade through trusted certification."
  }
];

const team = [
  {
    name: "Team Member",
    role: "Leadership",
    image: TEAM_PLACEHOLDER
  },
  {
    name: "Team Member",
    role: "Product",
    image: TEAM_PLACEHOLDER
  },
  {
    name: "Team Member",
    role: "Engineering",
    image: TEAM_PLACEHOLDER
  },
  {
    name: "Team Member",
    role: "Operations",
    image: TEAM_PLACEHOLDER
  }
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="section-padding bg-slate-50 leaf-pattern">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge-green mb-4 inline-block">About Certisfy</span>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Modernizing export certification for 
              <span className="text-[#1F7A3A]"> Southern Africa</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Certisfy is a cloud-based platform designed to streamline phytosanitary certification 
              and export compliance management. We're building the infrastructure that powers 
              trusted agricultural trade.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#FFFBEB] rounded-full">
              <Leaf className="w-5 h-5 text-[#C8A24A]" />
              <span className="text-[#A8842E] font-medium">A subsidiary of AGRIN</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section data-testid="mission" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="badge-gold mb-4 inline-block">Our Mission</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Making compliance simple, trade seamless
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Agricultural exports are the backbone of many Southern African economies. 
                Yet the certification processes that enable international trade have long 
                been manual, slow, and inconsistent.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We're on a mission to change that. Certisfy brings together national plant 
                protection organizations, inspection teams, and exporters on a single platform 
                that makes phytosanitary certification efficient, transparent, and trustworthy.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="card-highlight">
                <Target className="w-8 h-8 text-[#C8A24A] mb-4" />
                <h3 className="font-bold text-white text-lg mb-2">National Scale</h3>
                <p className="text-green-100 text-sm">Built for country-wide deployment and oversight</p>
              </div>
              <div className="card-base">
                <Globe className="w-8 h-8 text-[#1F7A3A] mb-4" />
                <h3 className="font-bold text-slate-900 text-lg mb-2">Trade Ready</h3>
                <p className="text-slate-600 text-sm">Certificates trusted by international partners</p>
              </div>
              <div className="card-base">
                <Users className="w-8 h-8 text-[#1F7A3A] mb-4" />
                <h3 className="font-bold text-slate-900 text-lg mb-2">All Stakeholders</h3>
                <p className="text-slate-600 text-sm">Tools for exporters, inspectors, and regulators</p>
              </div>
              <div className="card-base bg-slate-50">
                <Shield className="w-8 h-8 text-[#1F7A3A] mb-4" />
                <h3 className="font-bold text-slate-900 text-lg mb-2">Compliance First</h3>
                <p className="text-slate-600 text-sm">Meeting international standards and regulations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="values" className="section-padding bg-slate-50">
        <div className="container-custom">
          <SectionHeader 
            badge="Our Values"
            title="What guides us"
            subtitle="The principles that shape how we build Certisfy and serve our users"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {values.map((value, index) => (
              <div key={index} className="text-center" data-testid={`value-${index}`}>
                <div className="w-16 h-16 rounded-full bg-[#F0FDF4] flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-[#1F7A3A]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section data-testid="team" className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader 
            badge="Our Team"
            title="People behind Certisfy"
            subtitle="A dedicated team bringing together expertise in agriculture, technology, and compliance"
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {team.map((member, index) => (
              <div key={index} className="text-center group" data-testid={`team-member-${index}`}>
                <div className="relative mb-4 overflow-hidden rounded-2xl">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-64 object-cover group-hover:scale-105"
                    style={{ transition: 'transform 0.3s' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-left">
                    <p className="font-bold text-white">{member.name}</p>
                    <p className="text-sm text-slate-300">{member.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-8">
            Team profiles coming soon
          </p>
        </div>
      </section>

      {/* AGRIN Connection */}
      <section className="py-16 bg-[#1F7A3A]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center text-white">
            <Leaf className="w-12 h-12 mx-auto mb-4 text-[#C8A24A]" />
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Part of the AGRIN family
            </h2>
            <p className="text-green-100 text-lg">
              As a subsidiary of AGRIN, Certisfy benefits from deep expertise in agricultural 
              systems and a commitment to advancing Southern Africa's agricultural sector through 
              technology and innovation.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTAStrip 
        title="Join us in transforming agricultural trade"
        subtitle="Partner with Certisfy to modernize your certification processes."
        variant="white"
      />
    </div>
  );
}
